'use strict';
import { isFunction, isNeString, isObject } from '@aretheyhappy/utils';
import Flash from '../Flash';
const MODAL_CLOSE_EVENT = 'global-modal:close';
const MODAL_OPEN_EVENT = 'global-modal:open';
/**
 * Definition for a modal to be displayed
 */
class ModalConfig {
    constructor() {
        // Vue Component to render
        this.comp = false;
        // Function to call when modal is closed
        this.next = false;
        // Array of company permissions that need to be checked
        this.perm = false;
        // Props for the modal
        this.props = false;
    }
    clone() {
        const modalConfig = new ModalConfig();
        modalConfig.comp = this.comp;
        modalConfig.next = this.next;
        modalConfig.perm = this.perm;
        modalConfig.props = this.props;
        return modalConfig;
    }
    setComponent(comp) { this.comp = comp; return this; }
    setNextFunction(next) { this.next = next; return this; }
    /**
     * Data payload for the modal
     * @param data
     * @returns
     * @deprecated use Props instead
     */
    setData(data) {
        if (!isObject(this.props) || this.props === false)
            this.props = {};
        this.props.modaldata = data;
        return this;
    }
    /**
     * @deprecated permission checks should not be handled by the modal service
     */
    setPerm(perm) { this.perm = perm; return this; }
    setProps(props) { this.props = props; return this; }
}
/**
 * Open a global modal. App must have a GlobalModal.vue on the page somewhere.
 */
class ModalService {
    /**
     * Check if the there is a registered modal for the sent value
     * @param type
     * @returns
     */
    static hasRegisteredModal(type) {
        return this.MODAL_MAP.has(type);
    }
    /**
     * Fetch a registered modal config
     * @param type
     * @returns
     */
    static getRegisteredModal(type) {
        if (!this.hasRegisteredModal(type)) {
            console.warn(`ModalService.getRegisteredModal does not have a ModalConfig for ${type}`);
            return;
        }
        return this.MODAL_MAP.get(type)?.clone();
    }
    /**
     * Register a new modal config into the modal map
     * @param type
     * @param config
     */
    static registerModalConfig(type, config) {
        if (!isNeString(type)) {
            console.error('ModalService.registerModalConfig: type must be a non empty string');
            return;
        }
        // Modal already registered
        if (this.hasRegisteredModal(type))
            return;
        if (config instanceof ModalConfig) {
            this.MODAL_MAP.set(type, config);
        }
        else {
            console.error('ModalService.registerModalConfig: config must be of type ModalConfig');
        }
    }
    /**
     * Remove a modal config from the global map
     * @param type
     * @returns
     */
    static unregisterModalConfig(type) {
        if (!isNeString(type)) {
            console.error('ModalService.unregisterModalConfig: type must be a non empty string');
            return;
        }
        this.MODAL_MAP.delete(type);
    }
    /**
     * Returns whether or not a modal is in use
     * @returns
     */
    static hasModal() {
        return this.has_modal;
    }
    /**
     * Set modal usage
     * @param {boolean} val
     */
    static setHasModal(val) {
        this.has_modal = val;
    }
    /**
     * Open a new modal
     * @param {object} config
     * @param {object} data
     * @param {boolean | Function} next
     * @deprecated use openModal
     * @returns
     */
    static open(config, data = {}, next = false) {
        const cfg = Object.assign({}, {
            comp: false,
            next: false,
            data: false,
            perm: false, //  Array of company permissions that need to be checked
        }, config, { data, next });
        const modalConfig = new ModalConfig()
            .setComponent(cfg.comp)
            .setNextFunction(cfg.next)
            .setData(cfg.data)
            .setPerm(cfg.perm);
        this.has_modal = true;
        Flash.publish(MODAL_OPEN_EVENT, modalConfig);
    }
    /**
     * Open a new modal
     * @param config
     */
    static openModal(config) {
        this.has_modal = true;
        Flash.publish(MODAL_OPEN_EVENT, config);
    }
    /**
     * Open a new modal based on a registered config
     * @param type
     * @param props
     */
    static openRegisteredModal(type, props = {}, nextFunc) {
        if (!isNeString(type)) {
            console.error('ModalService.openRegisteredModal: type must be a non empty string');
            return;
        }
        if (props !== undefined && !isObject(props)) {
            console.error('ModalService.openRegisteredModal: props must be an object if provided');
            return;
        }
        const config = this.MODAL_MAP.get(type);
        if (config === undefined) {
            console.error(`ModalService.openRegisteredModal: no registered config for ${type}`);
            return;
        }
        const clonedConfig = config.clone();
        // Update Props
        clonedConfig.setProps(Object.assign({}, isObject(clonedConfig.props) ? clonedConfig.props : {}, props));
        // Set next function if provided
        if (nextFunc !== undefined && isFunction(nextFunc)) {
            clonedConfig.setNextFunction(nextFunc);
        }
        this.openModal(clonedConfig);
    }
    /**
     * Close current modal
     */
    static close() {
        this.has_modal = false;
        Flash.publish(MODAL_CLOSE_EVENT);
    }
}
ModalService.has_modal = false;
// Registry map for global modals to make them easier to open and avoid circular dependency cases.
ModalService.MODAL_MAP = new Map();
export { ModalService, ModalConfig, MODAL_CLOSE_EVENT, MODAL_OPEN_EVENT, };
export default ModalService;
//# sourceMappingURL=ModalService.js.map